import React, { useState, useContext, useRef, useEffect } from "react";
import { ChatContext } from "src/scenes/Chat/ChatContext";
import styled from "styled-components";
import svg_reply from "src/img/chatBubbleActionIcons/reply.svg";
import svg_ellipsis from "src/img/chatBubbleActionIcons/menu.svg";
import svg_edit from "src/img/chatBubbleActionIcons/edit.svg";
import svg_copy from "src/img/chatBubbleActionIcons/copy.svg";
import svg_delete from "src/img/chatBubbleActionIcons/delete.svg";
import DeleteConfirmation from "./DeleteConfirmation";
import { portalColors } from "src/components";

const EllipsisContainer = styled.div`
    position: absolute;
    top: ${({ isChatByCurrentUser, isTextMessage, isVideo }) => {
        if (isVideo) return isChatByCurrentUser ? "-60px" : "-60px";
        return isChatByCurrentUser
            ? isTextMessage
                ? "-150px"
                : "-110px"
            : "-70px";
    }};
    left: -165px;
    width: 165px;
    z-index: 99999;
    padding-bottom: 30px; /* this should touch the parent container for mouseLeave */
`;

const Actions = styled.div`
    background: ${portalColors.surface};
    border: 1px solid ${portalColors.border};
    border-radius: 10px;
    font-size: 14px;
    color: ${portalColors.text};
    box-shadow: 0px 3px 6pxrgba(129, 48, 48, 0.16);

    & li {
        display: flex;
        align-items: center;
        padding: 10px 14px;
        border-bottom: 1px solid ${portalColors.border};
        cursor: pointer;
    }

    & li:active {
        background-color: ${portalColors.surfaceTertiary};
    }

    & li:last-child{
        border-bottom: none;
    }

    & .list-text {
        flex-grow: 1;
    }

    & .icon {
        margin-left: auto;
    }
`;

const ChatBubbleActions = ({ matrixEvent, isChatByCurrentUser, isText, fileUrl, isVideo }) => {
    const [isEllipsisActive, setEllipsisActive] = useState(false);

    const onHandleMouseLeave = () => {
        setTimeout(() => {
            setEllipsisActive(false);
        }, 100);
    };

    return (
        <div className={"ChatBubbleActions"} onMouseLeave={onHandleMouseLeave} style={{ margin: "0 10px" }}>
            <ReplyIcon matrixEvent={matrixEvent} />

            <EllipsisIcon 
                matrixEvent={matrixEvent} 
                isChatByCurrentUser={isChatByCurrentUser}
                isEllipsisActive={isEllipsisActive}
                setEllipsisActive={setEllipsisActive}
                isText={isText}
                fileUrl={fileUrl}
                isVideo={isVideo} 
            />
        </div>
    );
};

const ReplyIcon = ({ matrixEvent }) => {
    const { 
        useSendMessage: ctxUseSendMessage
    } = useContext(ChatContext);

    const { onSetNewMessageInReplyToEventHandler } = ctxUseSendMessage;

    const handleOnClick = () => {
        onSetNewMessageInReplyToEventHandler(matrixEvent);
    };

    return (
        <span className="circle-radius" onClick={handleOnClick}>
            <img src={svg_reply} alt="Reply" />
        </span>
    );
};

const EllipsisIcon = ({ matrixEvent, isEllipsisActive, setEllipsisActive, isChatByCurrentUser, isText, fileUrl, isVideo }) => {

    const handleOnClick = () => {
        setEllipsisActive(isEllipsisActive => !isEllipsisActive);
    };

    return (
        <>
            <span className="circle-radius" onClick={handleOnClick}>
                <img src={svg_ellipsis} alt="More actions" />
            </span>
            
            <div style={{ position: "relative" }} >
                { isEllipsisActive &&
                    <EllipsisContainer isChatByCurrentUser={isChatByCurrentUser} isTextMessage={isText} isVideo={isVideo} >
                        <Actions>
                            { isChatByCurrentUser && isText && <EditIcon matrixEvent={matrixEvent} /> }
                            {!isVideo && <CopyIcon matrixEvent={matrixEvent} fileUrl={fileUrl} isText={isText} />}

                            { isChatByCurrentUser && <DeleteIcon matrixEvent={matrixEvent} /> }
                        </Actions>
                    </EllipsisContainer>
                }
            </div>
        </>
    );
};

const EditIcon = ({ matrixEvent }) => {
    
    const { 
        useSendMessage: ctxUseSendMessage
    } = useContext(ChatContext);

    const { onSetEditMessageEventHandler } = ctxUseSendMessage;

    const handleOnClick = () => {
        onSetEditMessageEventHandler(matrixEvent);
    };

    return (
        <li onClick={handleOnClick}>
            <span className="list-text">Edit</span>
            <img className="icon" src={svg_edit} alt="Edit" />
        </li>
    );
};

const CopyIcon = ({ matrixEvent, fileUrl, isText }) => {
    const [hasCopied, setHasCopied] = useState(false);
    const timeoutRef = useRef();

    useEffect(() => {
        return () => clearTimeout(timeoutRef.current);
    }, []);

    const showCopied = () => {
        setHasCopied(true);
        timeoutRef.current = setTimeout(() => setHasCopied(false), 1000);
    };

    const handleTextCopy = () => {
        navigator.clipboard.writeText(matrixEvent.getContent().body);
        showCopied();
    };

    const handleImageCopy = async () => {
        if (!fileUrl) return;

        try {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = fileUrl;

            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
            });

            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);

            const pngBlob = await new Promise((resolve) =>
                canvas.toBlob(resolve, "image/png")
            );

            await navigator.clipboard.write([new ClipboardItem({ "image/png": pngBlob })]);
            showCopied();
        } catch (err) {
            console.error("Failed to copy image:", err);
        }
    };

    return (
        <li onClick={isText ? handleTextCopy : handleImageCopy}>
            {hasCopied ? (
                "Copied to clipboard"
            ) : (
                <>
                    <span className="list-text">Copy</span>
                    <img className="icon" src={svg_copy} alt="Copy" />
                </>
            )}
        </li>
    );
};

const DeleteIcon = ({ matrixEvent }) => {
    const { matrixClient, useSendMessage: ctxUseSendMessage } = useContext(ChatContext);
    const { onResetInputMessageEvents } = ctxUseSendMessage;

    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [isDeletingChat, setIsDeletingChat] = useState(false);

    const handleChatDeletion = async () => {
        setIsDeletingChat(true);
        try {
            const roomId = matrixEvent.getRoomId();
            const eventId = matrixEvent.getId();
            await matrixClient.redactEvent(roomId, eventId);
            onResetInputMessageEvents();
        } catch (error) {
            console.error("Error deleting message:", error);
        } finally {
            setIsDeletingChat(false);
            setShowConfirmationModal(false);
        }
    };

    return (
        <>
            <li onClick={() => setShowConfirmationModal(true)}>
                <span className="list-text">Delete</span>
                <img className="icon" src={svg_delete} alt="Delete" />
            </li>

            <DeleteConfirmation
                isDeletingChat={isDeletingChat}
                showDialog={showConfirmationModal}
                handleClose={() => setShowConfirmationModal(false)}
                confirm={handleChatDeletion}
                type="message"
            />
        </>
    );
};

export default ChatBubbleActions;
